"use client";

import { useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";

import { TopBar } from "@/components/scirix/top-bar";
import { useWorkspaceRouteNamespace } from "@/components/workspaces/workspace-route-context";
import { resolveWorkspaceRoute } from "@/config/workspace-routes";
import { useAvailableWorkspaceTabs } from "@/hooks/useNamespaceFeatureFlags";
import { useActiveNamespaceSlug } from "@/hooks/useNamespaces";
import { useWorkspaceTabsStore } from "@/state/workspace-tabs-store";
import type { NamespaceId } from "@/types/namespaces";

type WorkspaceMissionTopBarProps = {
  activeNamespaceId: NamespaceId;
};

export function WorkspaceMissionTopBar({ activeNamespaceId }: WorkspaceMissionTopBarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const routeNamespace = useWorkspaceRouteNamespace();
  const ambientSlug = useActiveNamespaceSlug();
  const activeNamespaceSlug = routeNamespace?.slug ?? ambientSlug;
  const closedTabs = useWorkspaceTabsStore((state) => state.closedTabs);
  const reopenTab = useWorkspaceTabsStore((state) => state.reopenTab);
  // Feature-gated tabs are absent when their feature is disabled for the
  // active namespace, so the switcher can't reopen a hidden workspace.
  const availableTabs = useAvailableWorkspaceTabs(routeNamespace?.namespaceId);

  const workspaces = useMemo(
    () =>
      availableTabs.map((tab) => ({
        id: tab.id,
        name: tab.label,
        isOpen: tab.id === activeNamespaceId || !closedTabs.includes(tab.id),
      })),
    [activeNamespaceId, availableTabs, closedTabs],
  );

  const handleSelectWorkspace = (namespaceId: NamespaceId) => {
    reopenTab(namespaceId);
    const destination = resolveWorkspaceRoute(namespaceId, activeNamespaceSlug ?? undefined);
    if (!destination || destination === pathname) {
      return;
    }

    router.push(destination);
  };

  const missionAriaLabel = closedTabs.length
    ? `Workspace mission controls – ${closedTabs.length} closed tab${closedTabs.length === 1 ? "" : "s"} ready to reopen`
    : "Workspace mission controls";

  return (
    <TopBar
      ariaLabel={missionAriaLabel}
      workspaces={workspaces}
      activeNamespaceId={activeNamespaceId}
      onSelectWorkspace={handleSelectWorkspace}
    />
  );
}
